/**
 * 문제집 ZIP 파일 업로드 컴포넌트
 * - ZIP 파일 선택 (Excel + 정답 이미지)
 * - 파일 업로드 및 결과 표시
 * - 로딩 상태 관리
 */

import React, { useState } from 'react';
import { Upload, Button, message, Alert, Space } from 'antd';
import { FileZipOutlined, UploadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { workbookService } from '../../services/workbookService';
import { ValidationResult } from './ValidationResult';
import type { WorkbookUploadResponse } from '../../types/workbook';

/**
 * WorkbookUpload 컴포넌트
 * ZIP 파일 업로드 UI 및 결과 표시
 */
export const WorkbookUpload: React.FC = () => {
  // 선택된 파일
  const [file, setFile] = useState<File | null>(null);

  // 업로드 진행 상태
  const [loading, setLoading] = useState(false);

  // 업로드 결과
  const [result, setResult] = useState<WorkbookUploadResponse | null>(null);

  /**
   * 파일 선택 시 처리
   * - 자동 업로드 방지 (beforeUpload에서 false 반환)
   * - ZIP 파일 검증
   */
  const handleBeforeUpload = (file: File): boolean => {
    // ZIP 파일 검증
    if (!file.name.endsWith('.zip')) {
      message.error('ZIP 파일만 업로드 가능합니다');
      return false;
    }

    // 파일 크기 검증 (100MB)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      message.error('파일 크기는 100MB 이하여야 합니다');
      return false;
    }

    // 파일 저장
    setFile(file);

    // 이전 결과 초기화
    setResult(null);

    // 자동 업로드 방지
    return false;
  };

  /**
   * 파일 제거 시 처리
   */
  const handleRemove = () => {
    setFile(null);
    setResult(null);
  };

  /**
   * 업로드 버튼 클릭 시 처리
   */
  const handleUpload = async () => {
    if (!file) {
      message.error('파일을 선택해주세요');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // API 호출
      const uploadResult = await workbookService.uploadWorkbookZip(file);

      // 결과 저장
      setResult(uploadResult);

      // 성공 시 메시지 표시
      if (uploadResult.success) {
        message.success('업로드가 완료되었습니다');

        // 파일 초기화 (재업로드 가능하도록)
        setFile(null);
      } else {
        message.error('업로드에 실패했습니다');
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error('업로드 중 오류가 발생했습니다');

      setResult({
        success: false,
        errors: ['서버 연결 실패. 잠시 후 다시 시도해주세요.'],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* 안내 메시지 */}
      <Alert
        message="ZIP 파일 업로드 안내"
        description={
          <div>
            <p>다음 구조의 ZIP 파일을 업로드해주세요:</p>
            <ul style={{ marginBottom: 0 }}>
              <li><strong>Excel 파일</strong>: 문제집 정보 및 문제 답안 데이터</li>
              <li><strong>answers/ 폴더</strong>: T타입 정답 이미지 (파일명: 페이지_문제번호.png)</li>
            </ul>
            <p style={{ marginTop: 8, marginBottom: 0 }}>
              <InfoCircleOutlined /> 최대 파일 크기: 100MB
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* 파일 업로드 영역 */}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Upload
          beforeUpload={handleBeforeUpload}
          onRemove={handleRemove}
          accept=".zip"
          maxCount={1}
          fileList={file ? [{ uid: '-1', name: file.name, status: 'done' } as UploadFile] : []}
        >
          <Button icon={<FileZipOutlined />} disabled={loading}>
            ZIP 파일 선택
          </Button>
        </Upload>

        {/* 선택된 파일 정보 */}
        {file && (
          <div style={{ color: '#666' }}>
            선택된 파일: <strong>{file.name}</strong> ({(file.size / (1024 * 1024)).toFixed(2)} MB)
          </div>
        )}

        {/* 업로드 버튼 */}
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={handleUpload}
          disabled={!file || loading}
          loading={loading}
          size="large"
        >
          {loading ? '업로드 중...' : '업로드'}
        </Button>

        {/* 업로드 결과 표시 */}
        <ValidationResult result={result} />
      </Space>
    </div>
  );
};
